// Older app and web builds saved these in place of missing location fields.
const FILLER_TEXTS = ['united states', 'estados unidos']
const FILLER_PLACE_ID = 'ChIJCzYy5IS16lQRQrfeQ5K5Oxw'
const FILLER_LATITUDE = 37.09024
const FILLER_LONGITUDE = -95.71289
const COORDINATE_TOLERANCE = 0.0001

const GOOGLE_MAPS_SEARCH_URL = 'https://www.google.com/maps/search/?api=1'

function realText(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const text = String(value).trim()
  if (!/[\p{L}\p{N}]/u.test(text)) return null
  return FILLER_TEXTS.includes(text.toLowerCase()) ? null : text
}

function toCoordinate(value) {
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim()) return Number(value)
  return NaN
}

export function realCoordinates(latitude, longitude) {
  const lat = toCoordinate(latitude)
  const lng = toCoordinate(longitude)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  if (lat === 0 && lng === 0) return null
  if (
    Math.abs(lat - FILLER_LATITUDE) < COORDINATE_TOLERANCE &&
    Math.abs(lng - FILLER_LONGITUDE) < COORDINATE_TOLERANCE
  ) {
    return null
  }
  return { latitude: lat, longitude: lng }
}

function realPlaceId(value) {
  const id = typeof value === 'string' ? value.trim() : ''
  return id && id !== FILLER_PLACE_ID ? id : null
}

// Filler and empty fields become null; null when nothing real is left.
export function realLocation(location) {
  if (!location || typeof location !== 'object') return null
  const coordinates = realCoordinates(location.latitude, location.longitude)
  const real = {
    name: realText(location.name),
    formatted_address: realText(location.formatted_address),
    city: realText(location.city),
    state: realText(location.state),
    zip: realText(location.zip),
    latitude: coordinates?.latitude ?? null,
    longitude: coordinates?.longitude ?? null,
    google_location_id: realPlaceId(location.google_location_id),
    google_description: realText(location.google_description),
  }
  const hasText =
    real.google_description ||
    real.name ||
    real.formatted_address ||
    real.city ||
    real.state ||
    real.zip
  // A pick of the country itself has its own center, so only the place ID and the text give it away.
  const countryPick =
    !hasText &&
    String(location.google_location_id ?? '').trim() === FILLER_PLACE_ID
  return hasText || (coordinates && !countryPick) ? real : null
}

export function hasRealLocation(location) {
  return realLocation(location) !== null
}

function addressText(real) {
  if (real.google_description) return real.google_description
  const parts = []
  const seen = new Set()
  ;[real.name, real.formatted_address, real.city, real.state].forEach(part => {
    if (!part) return
    const segments = part
      .split(',')
      .map(segment => segment.trim().toLowerCase())
      .filter(Boolean)
    if (segments.every(segment => seen.has(segment))) return
    parts.push(part)
    segments.forEach(segment => seen.add(segment))
  })
  return parts.join(', ')
}

function displayText(real) {
  return addressText(real) || real.zip || `${real.latitude}, ${real.longitude}`
}

export function locationDisplayText(location) {
  const real = realLocation(location)
  return real ? displayText(real) : ''
}

export function locationMapsUrl(location) {
  const real = realLocation(location)
  if (!real) return null
  const query =
    real.latitude !== null
      ? `${real.latitude},${real.longitude}`
      : displayText(real)
  let url = `${GOOGLE_MAPS_SEARCH_URL}&query=${encodeURIComponent(query)}`
  if (real.google_location_id) {
    url += `&query_place_id=${encodeURIComponent(real.google_location_id)}`
  }
  return url
}

function addressComponent(place, type) {
  const components = Array.isArray(place?.address_components)
    ? place.address_components
    : []
  return components.find(component => component.types?.includes(type))
}

export function placeAddressParts(place) {
  const city =
    addressComponent(place, 'locality') ??
    addressComponent(place, 'postal_town') ??
    addressComponent(place, 'sublocality')
  const state = addressComponent(place, 'administrative_area_level_1')
  const zip = addressComponent(place, 'postal_code')
  return {
    city: city?.long_name ?? '',
    state: state?.short_name ?? '',
    zip: zip?.long_name ?? '',
  }
}

export function parseGooglePlaceToEventLocation(place) {
  // A pick of the country itself would save the filler text and place ID.
  if (!realText(place?.description) || place.place_id === FILLER_PLACE_ID) {
    return null
  }

  const description = place.description
  return {
    formatted_address: description,
    name: description,
    ...placeAddressParts(place),
    latitude: Number.parseFloat(place.geometry.location.lat),
    longitude: Number.parseFloat(place.geometry.location.lng),
    google_location_id: place.place_id,
    google_description: description,
  }
}
